import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'react-fast-compare';
import { includes } from 'lodash';
import { message } from 'antd';
import { RootState } from '../../../../store/modules';
import { asyncSimpleModal, checkIsNonSelectedList } from '../../../../lib/common';
import { DATE_FORMAT, INVALID_MINIMUM_SEARCH_WORD_MESSAGE } from '../../../../constants/service/common/utilConstants';
import { widgetActions } from '../../../../store/modules/widget';
import { WIDGET_ALL_LIST_SEARCH_DATA_MODEL } from '../../../../store/modules/widget/models';
import { _LIST_PAGINATION_MODEL } from '../../../../store/modules/app/models';
import { INVALID_REQUIRED_SELECT_WIDGET_MESSAGE } from '../../../../constants/service/invalidMsg/widgetInvalidMsg';
import { WIDGET_EXPOSURE_TYPE } from '../../../../constants/service/app/widgetConstants';
import { setDateFormat } from '../../../../lib/moment';
import WidgetAllListSearchBox from '../WidgetAllListSearchBox';
import WidgetAllList from '../WidgetAllList';
import { ValueOf } from '../../../../types/common/utils';
import './WidgetAllListWrap.scss';
import { useMutation } from '@tanstack/react-query';
import widgetApiV2 from '../../../../lib/api/widget/WidgetApiV2';
import toastMsg from '../../../../lib/toastMsg';
import widgetToastMsg from '../../../../constants/service/toastMsg/widgetToastMsg';
import { ErrorResponseType, extractErrorMessage } from '../../../../lib/utils/checkErrorResponse';

export type WidgetItem = {
  id: number;
  no: number | null;
  widgetType: number;
  layoutType: number;
  mainTitle: string;
  subTitle: string;
  memo: string;
  exposureStartDate: string;
  exposureEndDate: string;
  status: number;
  linkedPageId: number | null;
  linkedPageType: number | null;
  linkedPageTitle: string | null;
  createDate: string;
};

export type WidgetAllListType = RootState['widget']['widgetAllList'];

export type WidgetAllListSearchData = WidgetAllListType['widgetList']['searchOption'];
export type WidgetAllListPagination = WidgetAllListType['widgetList']['pagination'];

export type WidgetAllListRequest = {
  searchOption?: WidgetAllListSearchData & {
    searchKeyword: WidgetAllListSearchData['searchKeyword'] & { text?: string };
  };
  pagination?: WidgetAllListPagination;
  isInit?: boolean;
};

const WidgetAllListWrap = (): JSX.Element => {
  const dispatch = useDispatch();
  const widgetAllList = useSelector<RootState, WidgetAllListType>((state) => state.widget.widgetAllList);
  const { widgetExposurePeriodBatchInfo, widgetList, selectedWidgetIdList, originalWidgetList } = widgetAllList;

  const { mutateAsync } = useMutation(widgetApiV2.deleteWidget);

  const getWidgetPreviewData = (payload: { widgetId: number }): void => {
    dispatch(widgetActions.getWidgetPreviewData(payload));
  };

  const getWidgetAllList = (payload: WidgetAllListRequest): void => {
    dispatch(widgetActions.getWidgetAllList(payload));
  };

  const getList = async (data: WidgetAllListRequest): Promise<void> => {
    const { list, searchOption, pagination } = widgetList;

    /**
     *  위젯 리스트에서 변경된 내용이 있다면,
     *  변경된 내용이 있다는 메시지를 보여주고
     *  진행을 하도록 함
     *  (위젯 전체리스트에 처음에 접근하거나, 검색을 초기화 할 때는
     *  메시지를 보여주지 않음.)
     */
    const isUpdateList = !isEqual(list, originalWidgetList);
    if (isUpdateList && !data.isInit) {
      const confirmResult = await asyncSimpleModal({
        type: 'confirm',
        title: '저장하지 않은 변경된 위젯',
        message:
          '위젯 리스트에서 변경된 위젯이 있지만,\n 저장이 되지않았습니다.\n 검색 및 페이지를 이동하면 변경된 사항이 초기화됩니다.',
      });

      if (!confirmResult) return;
    }

    /**
     * 검색어 유효성 체크
     */
    if (data.searchOption) {
      /**
       * 두 글자 이상
       */
      const { searchKeyword } = data.searchOption;
      if (searchKeyword && searchKeyword.text && (searchKeyword.text === '' || searchKeyword.text.length < 2)) {
        message.error(INVALID_MINIMUM_SEARCH_WORD_MESSAGE);
        return;
      }
    }

    getWidgetAllList({
      searchOption,
      pagination,
      ...data,
    });
  };

  const init = (): void => {
    getList({
      searchOption: WIDGET_ALL_LIST_SEARCH_DATA_MODEL,
      pagination: _LIST_PAGINATION_MODEL,
      isInit: true,
    });

    dispatch(widgetActions.resetWidgetListSearchData());
  };

  const handleChangeSearchOptionWidgetType = (widgetType: string): void => {
    dispatch(widgetActions.setSearchOptionWidgetType(widgetType));
  };

  //위젯 노출기간 일괄 변경하기
  const handleChangeBulkWidgetPeriod = (widgetExposureType: ValueOf<typeof WIDGET_EXPOSURE_TYPE>): void => {
    /**
     * 선택된 상품이 있는지 체크
     */
    const isSelected = checkIsNonSelectedList(selectedWidgetIdList, INVALID_REQUIRED_SELECT_WIDGET_MESSAGE);
    if (!isSelected) return;

    /**
     *  변경할 위젯 노출 기간
     */
    let targetWidgetExposureDate:
      | { exposureStartDate: string | null; exposureEndDate: string | null }
      | Record<string, never> = {};

    /**
     *  직접 설정
     */
    if (widgetExposureType === WIDGET_EXPOSURE_TYPE.DIRECT_SETTING) {
      targetWidgetExposureDate = {
        exposureStartDate: setDateFormat(widgetExposurePeriodBatchInfo.startDate, DATE_FORMAT.FULL_DASH_FORMAT),
        exposureEndDate: setDateFormat(widgetExposurePeriodBatchInfo.endDate, DATE_FORMAT.FULL_DASH_FORMAT),
      };
    } else if (widgetExposureType === WIDGET_EXPOSURE_TYPE.INFINITE) {
      /**
       *  무기한
       */
      targetWidgetExposureDate = {
        exposureStartDate: null,
        exposureEndDate: null,
      };
    }

    /**
     *  선택된 위젯 아이템의 노출기간을 변경함
     */
    const updatedWidgetAllList = (widgetList.list as WidgetItem[]).map((widget) => {
      if (includes(selectedWidgetIdList, widget.id)) {
        return {
          ...widget,
          ...targetWidgetExposureDate,
        };
      }
      return widget;
    });

    dispatch(widgetActions.setWidgetAllList(updatedWidgetAllList));
  };

  const saveWidgetAllList = async (): Promise<void> => {
    const confirmResult = await asyncSimpleModal({
      type: 'confirm',
      title: '변경된 위젯 저장',
      message: '정말로 저장을 하시겠습니까?',
    });

    if (!confirmResult) return;

    const { widgetList } = widgetAllList;

    /**
     *  서버에 보낼 때는 id, no, status, exposureStartDate, exposureEndDate 만 넘겨줌.
     *  위젯 상태가 활성(1)인 경우에만 순서가 부여됨
     */
    const updateAllWidgetList = (widgetList.list as WidgetItem[]).map((item) => {
      return {
        id: item.id,
        status: item.status,
        no: item.no,
        exposureStartDate: item.exposureStartDate
          ? setDateFormat(item.exposureStartDate, DATE_FORMAT.FULL_DASH_FORMAT)
          : null,
        exposureEndDate: item.exposureEndDate
          ? setDateFormat(item.exposureEndDate, DATE_FORMAT.FULL_DASH_FORMAT)
          : null,
      };
    });

    dispatch(
      widgetActions.saveUpdatedAllWidgetList({
        widgetList: updateAllWidgetList,
      }),
    );
  };

  const deleteWidget = async (widgetId: number): Promise<void> => {
    const confirmResult = await asyncSimpleModal({
      type: 'confirm',
      title: '위젯 삭제',
      message: '해당 위젯을 삭제합니다. 계속 진행하시겠습니까?',
    });

    if (!confirmResult) return;

    try {
      await mutateAsync(widgetId);
      dispatch(widgetActions.deleteWidget(widgetId));
      toastMsg.success(widgetToastMsg.DELETE_WIDGET_REQUEST_SUCCESS);
    } catch (error) {
      toastMsg.error(widgetToastMsg.DELETE_WIDGET_REQUEST_ERROR, extractErrorMessage(error as ErrorResponseType));
    }
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <div className="widget-all-list-wrap">
      {/* 위젯 검색 박스*/}
      <WidgetAllListSearchBox
        searchOption={widgetList.searchOption}
        onChangeWidgetTypeSearchOption={handleChangeSearchOptionWidgetType}
        getList={getList}
        onClear={init}
      />

      {/* 위젯 전체 리스트 */}
      <WidgetAllList
        widgetExposurePeriodBatchInfo={widgetExposurePeriodBatchInfo}
        widgetList={widgetList.list}
        pagination={widgetList.pagination}
        originalWidgetList={originalWidgetList}
        selectedWidgetIdList={selectedWidgetIdList}
        getWidgetPreviewData={getWidgetPreviewData}
        setInitialWidgetAllList={init}
        onChangeBulkWidgetPeriod={handleChangeBulkWidgetPeriod}
        saveWidgetAllList={saveWidgetAllList}
        getList={getList}
        deleteWidget={deleteWidget}
      />
    </div>
  );
};

export default WidgetAllListWrap;
